/**
 * <euro-bundle>
 *
 * Companion component for snippets/euro-bundle.liquid.
 * Modeled on <protector-upsell> — same form-interception pattern.
 *
 * Responsibilities:
 *  - Intercepts the main product form's submit when "Yes" is chosen and
 *    POSTs both items in one /cart/add.js call. Both lines get a shared
 *    token: the main line as `_euro_bundle`, the gift line as
 *    `_euro_bundle_gift`. euro-bundle-guard.js uses the token to keep
 *    the pair honest in the cart.
 *  - One-time claim: on init (and after every cart add) it checks the
 *    cart; if a gift line for this gift product already exists, the
 *    "Yes" option is disabled and a "already claimed" note is shown.
 */
if (!customElements.get('euro-bundle')) {
  class EuroBundle extends HTMLElement {
    connectedCallback() {
      try {
        this.giftVariantId = Number(this.dataset.giftVariantId);
        this.giftProductId = Number(this.dataset.giftProductId);
        this.giftAvailable = this.dataset.giftAvailable === 'true';
        this.form = document.getElementById(this.dataset.productFormId);
        this._formAttached = false;
        this._claimed = false;

        // ---- Size auto-assignment (mirrors protector-upsell) ----
        this.giftVariants = this._parseJSON(this.dataset.giftVariants) || {};
        this.overrideMap = this._parseJSON(this.dataset.overrideMap) || null;
        this.defaultMap = this._parseJSON(this.dataset.defaultMap) || null;
        this.legacyVariantId = this.giftVariantId; // first-available fallback
        this.legacyAvailable = this.giftAvailable;

        this.sizeSelect = this.querySelector('[data-euro-bundle-size]');
        this.sizeWrap = this.querySelector('[data-euro-bundle-size-wrap]');
        this._userPickedSize = false;
        this._wireSizeSelector();

        this._wireFormInterception();
        this._wireVariantListeners();
        this._resolveGiftForSize(this._currentMainSize());
        this._checkAlreadyClaimed();

        this._boundClaimCheck = () => this._checkAlreadyClaimed();
        document.addEventListener('cart:item-added', this._boundClaimCheck);
        document.addEventListener('line-item:change:end', this._boundClaimCheck);
      } catch (err) {
        console.warn('[euro-bundle] init failed', err);
        this.hidden = true;
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:item-added', this._boundClaimCheck);
      document.removeEventListener('line-item:change:end', this._boundClaimCheck);
      if (this._boundVariantHandler) {
        document.removeEventListener('product:variant-change', this._boundVariantHandler);
        document.removeEventListener('variant:change', this._boundVariantHandler);
        document.removeEventListener('change', this._boundVariantChangeFallback);
      }
    }

    /* ------------------------------------------------------------------ */
    /*  Size auto-assignment                                               */
    /* ------------------------------------------------------------------ */

    _parseJSON(raw) {
      if (!raw || raw === 'null') return null;
      try { return JSON.parse(raw); } catch (e) { return null; }
    }

    _mapLookup(map, key) {
      if (!map || key == null) return null;
      if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
      return null;
    }

    /**
     * The theme renders variant inputs OUTSIDE the form (associated via
     * [form="..."]), so scan fieldset radios + selects, preferring a value
     * that actually resolves (mapping entry or gift size) — same approach
     * as protector-upsell.
     */
    _currentMainSize() {
      const candidates = [];
      const formId = this.dataset.productFormId;
      document.querySelectorAll('fieldset input[type="radio"]:checked, variant-radios input[type="radio"]:checked').forEach((r) => {
        if (!r.value) return;
        const owner = r.closest('form');
        if (owner && owner.id !== formId) return;
        candidates.push(r.value);
      });
      document.querySelectorAll('variant-selects select, .product-form__input select').forEach((s) => {
        if (s.value) candidates.push(s.value);
      });
      const resolves = (v) =>
        this._mapLookup(this.overrideMap, v) !== null ||
        this._mapLookup(this.defaultMap, v) !== null ||
        Object.prototype.hasOwnProperty.call(this.giftVariants, v);
      const direct = candidates.find(resolves);
      if (direct) return direct;
      if (candidates.length) return candidates[0];
      return this.dataset.mainDefaultSize || null;
    }

    /**
     * override exact -> default exact -> override * -> default * ->
     * identity -> legacy first-available. A resolved target size that is
     * sold out => unavailable (no silent size substitution).
     */
    _resolveGiftForSize(mainSize) {
      let targetSize =
        this._mapLookup(this.overrideMap, mainSize) ??
        this._mapLookup(this.defaultMap, mainSize) ??
        this._mapLookup(this.overrideMap, '*') ??
        this._mapLookup(this.defaultMap, '*');

      if (targetSize == null && mainSize != null &&
          Object.prototype.hasOwnProperty.call(this.giftVariants, mainSize)) {
        targetSize = mainSize; // identity match
      }

      if (this._userPickedSize && this.sizeSelect) {
        // customer chose a gift size manually — keep it
        const manual = this.giftVariants[this._selectedGiftSize()];
        if (manual) {
          this.giftVariantId = manual.id;
          this.giftAvailable = !!manual.available;
          this._applyAvailabilityState();
          return;
        }
      }
      if (targetSize != null) {
        const entry = this.giftVariants[targetSize];
        if (entry) {
          this.giftVariantId = entry.id;
          this.giftAvailable = !!entry.available;
          this._syncSizeSelect(targetSize);
        } else {
          // mapping points at a size the gift product doesn't have
          this.giftVariantId = null;
          this.giftAvailable = false;
        }
      } else {
        // nothing resolves — legacy behavior (first available variant)
        this.giftVariantId = this.legacyVariantId;
        this.giftAvailable = this.legacyAvailable;
      }
      this._applyAvailabilityState();
    }

    _applyAvailabilityState() {
      const yes = this._yesRadio();
      const no = this._noRadio();
      const note = this.querySelector('[data-euro-bundle-unavailable]');
      const usable = this.giftAvailable && !this._claimed;
      this.classList.toggle('is-unavailable', !this.giftAvailable);
      if (note) note.hidden = this.giftAvailable;
      if (yes) {
        yes.disabled = !usable;
        if (!usable && yes.checked && no) {
          yes.checked = false;
          no.checked = true;
        }
      }
      if (no && !this.giftAvailable) no.disabled = false;
      if (this.sizeWrap) {
        const yesNow = this._yesRadio();
        this.sizeWrap.hidden = !(yesNow && yesNow.checked && !yesNow.disabled);
      }
    }

    _selectedGiftSize() {
      if (!this.sizeSelect) return null;
      const checked = this.sizeSelect.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    }

    _wireSizeSelector() {
      if (!this.sizeSelect) return;
      // show the selector only while "Yes" is chosen
      const sync = () => {
        const yes = this._yesRadio();
        if (this.sizeWrap) this.sizeWrap.hidden = !(yes && yes.checked && !yes.disabled);
      };
      this.querySelectorAll('[data-euro-bundle-choice]').forEach((r) => r.addEventListener('change', sync));
      sync();
      this.sizeSelect.addEventListener('change', (e) => {
        if (!e.target || e.target.type !== 'radio') return;
        this._userPickedSize = true;
        this._updateSizeValueLabel(e.target.value);
        const entry = this.giftVariants[e.target.value];
        if (entry) {
          this.giftVariantId = entry.id;
          this.giftAvailable = !!entry.available;
        }
        this._applyAvailabilityState();
      });
    }

    _updateSizeValueLabel(value) {
      const label = this.querySelector('[data-euro-bundle-size-value]');
      if (label) label.textContent = value || '';
    }

    _syncSizeSelect(targetSize) {
      if (!this.sizeSelect || this._userPickedSize) return;
      if (targetSize != null && Object.prototype.hasOwnProperty.call(this.giftVariants, targetSize)) {
        const radio = this.sizeSelect.querySelector('input[value="' + CSS.escape(targetSize) + '"]');
        if (radio && !radio.disabled) {
          radio.checked = true;
          this._updateSizeValueLabel(targetSize);
        }
      }
    }

    _wireVariantListeners() {
      this._boundVariantHandler = () => this._resolveGiftForSize(this._currentMainSize());
      this._boundVariantChangeFallback = (e) => {
        if (!e.target) return;
        const t = e.target;
        if ((t.matches && (t.matches('fieldset input[type="radio"]') || t.matches('variant-selects select') || t.matches('.product-form__input select, .product-form__input input')))) {
          this._boundVariantHandler();
        }
      };
      document.addEventListener('product:variant-change', this._boundVariantHandler);
      document.addEventListener('variant:change', this._boundVariantHandler);
      document.addEventListener('change', this._boundVariantChangeFallback);
    }

    _yesRadio() { return this.querySelector('[data-euro-bundle-choice="yes"]'); }
    _noRadio()  { return this.querySelector('[data-euro-bundle-choice="no"]'); }

    async _checkAlreadyClaimed() {
      try {
        const res = await fetch(`${window.theme.routes.cart_url}.js`, {
          headers: { 'Accept': 'application/json' },
        });
        const cart = await res.json();
        const claimed = (cart.items || []).some((item) =>
          item.product_id === this.giftProductId &&
          item.properties && item.properties._euro_bundle_gift
        );
        this._setClaimedState(claimed);
      } catch (e) {
        // Network hiccup — leave the widget usable; the guard still
        // enforces one claim per cart server-side of the add.
      }
    }

    _setClaimedState(claimed) {
      this._claimed = !!claimed;
      const note = this.querySelector('[data-euro-bundle-claimed]');
      if (note) note.hidden = !claimed;
      this._applyAvailabilityState();
    }

    _wireFormInterception() {
      if (!this.form || this._formAttached) return;
      this._formAttached = true;
      // Capture phase so we can preempt <product-form>'s submit handler
      // when YES is selected.
      this.form.addEventListener('submit', this._onFormSubmit.bind(this), { capture: true });
    }

    /**
     * Companion contract: other upsell components (protector-upsell) call
     * this when they intercept the shared form, so one submission carries
     * every selected companion. Returns the gift line + the property to
     * stamp onto the MAIN item, or null when the gift isn't selected.
     */
    getCompanionItem() {
      const yes = this._yesRadio();
      if (!yes || !yes.checked || yes.disabled) return null;
      if (!this.giftAvailable || !this.giftVariantId) return null;
      const token = `eb-${this.giftVariantId}-${Date.now()}`;
      this._lastCompanionToken = token;
      return {
        item: { id: this.giftVariantId, quantity: 1, properties: { _euro_bundle_gift: token } },
        mainProperties: { _euro_bundle: token },
        onAdded: () => this._setClaimedState(true),
      };
    }

    async _onFormSubmit(event) {
      if (event._pandoCompanionsHandled) return; // another upsell already carries us
      const yes = this._yesRadio();
      if (!yes || !yes.checked || yes.disabled) return; // let normal handler run
      if (!this.giftAvailable || !this.giftVariantId) return;

      event._pandoCompanionsHandled = true;
      event.preventDefault();
      event.stopImmediatePropagation();

      const formData = new FormData(this.form);
      const mainVariantId = formData.get('id');
      const mainQty = parseInt(formData.get('quantity'), 10) || 1;
      if (!mainVariantId) return;

      // Shared token links the pair. Unique per add so two different
      // bundles never cross-match in the guard.
      const token = `eb-${mainVariantId}-${Date.now()}`;

      const mainProps = { _euro_bundle: token };
      // Forward any other line-item properties from the form.
      for (const [key, value] of formData.entries()) {
        const m = /^properties\[(.+)\]$/.exec(key);
        if (m) mainProps[m[1]] = value;
      }

      const items = [
        { id: Number(mainVariantId), quantity: mainQty, properties: mainProps },
        { id: this.giftVariantId, quantity: 1, properties: { _euro_bundle_gift: token } },
      ];

      // Collect companions from other upsell components on the same form
      // (e.g. Quatroflex) so a single submission carries everything.
      const companionCallbacks = [];
      document.querySelectorAll('protector-upsell').forEach((el) => {
        if (el.form !== this.form || typeof el.getCompanionItem !== 'function') return;
        const c = el.getCompanionItem();
        if (c && c.item) {
          items.push(c.item);
          if (c.mainProperties) Object.assign(items[0].properties, c.mainProperties);
          if (c.onAdded) companionCallbacks.push(c.onAdded);
        }
      });
      this._companionCallbacks = companionCallbacks;

      const sectionsToRender = ['cart-drawer', 'cart-bubble', 'main-cart'];

      this._setSubmittingState(true);
      try {
        const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items, sections: sectionsToRender, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (data.status && data.status >= 400) {
          console.warn('[euro-bundle] cart/add failed', data);
          this._setSubmittingState(false);
          this.form.submit(); // fall back to the normal single-item add
          return;
        }
        this._renderCartContents(data);
        this._setClaimedState(true);
        (this._companionCallbacks || []).forEach((cb) => { try { cb(); } catch (e) {} });
        this._companionCallbacks = null;
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: data.items?.[0] || data } }));
      } catch (err) {
        console.error('[euro-bundle] cart add error', err);
        this.form.submit();
      } finally {
        this._setSubmittingState(false);
      }
    }

    _setSubmittingState(isSubmitting) {
      const btns = document.querySelectorAll('.single-add-to-cart-button');
      btns.forEach((btn) => {
        btn.classList.toggle('loading', isSubmitting);
        btn.toggleAttribute('aria-disabled', isSubmitting);
      });
    }

    /** Mirrors protector-upsell's renderContents so drawer + bubble update inline. */
    _renderCartContents(parsedState) {
      const sections = parsedState.sections || {};
      const targets = [
        { id: 'Cart', selector: '.thb-cart-form', section: 'main-cart' },
        { id: 'Cart-Drawer', selector: '.cart-drawer', section: 'cart-drawer' },
        { id: 'cart-drawer-toggle', selector: '#cart-drawer-toggle', section: 'cart-bubble' },
      ];
      targets.forEach((t) => {
        const host = document.getElementById(t.id);
        if (!host || !sections[t.section]) return;
        const replace = host.querySelector(t.selector) || host;
        try {
          const html = new DOMParser().parseFromString(sections[t.section], 'text/html');
          const inner = html.querySelector(t.selector);
          if (inner) replace.innerHTML = inner.innerHTML;
        } catch (e) {
          console.warn('[euro-bundle] failed to render', t.section, e);
        }
      });
      const drawer = document.getElementById('Cart-Drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      drawer?.notesToggle?.();
      drawer?.removeProductEvent?.();
    }
  }

  customElements.define('euro-bundle', EuroBundle);
}